import {
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import {FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatSelect} from '@angular/material/select';
import {ReplaySubject, Subject, timer} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {GfxHttpSrv} from 'src/app/services/GfxHttpSrv';
import {filter} from 'underscore';
import {GfxMiscSrv} from '../../services/gfx.misc.service';

@Component({
    selector: 'gfx-ngx-mat-select-search',
    templateUrl: './gfx-ngx-mat-select-search.component.html',
    styleUrls: ['./gfx-ngx-mat-select-search.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => GfxNgxMatSelectSearchComponent),
            multi: true,
        },
    ],
})
export class GfxNgxMatSelectSearchComponent implements OnInit {
    @Input('selectConfig') selectConfig: ISelectNgxMat;
    @Output()
    selectedVal = new EventEmitter<any>();

    /** control for the MatSelect filter keyword */
    public selFilterCtrl: FormControl = new FormControl();

    /** list of elements filtered by search keyword */
    public filteredData: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

    @ViewChild('singleSelect', {static: true}) singleSelect: MatSelect;

    /** Subject that emits when the component has been destroyed. */
    protected _onDestroy = new Subject<void>();
    curData: any = [];

    // isloaded: boolean = true;

    constructor(private http: GfxHttpSrv,
                private misc: GfxMiscSrv) {
    }

    ngOnInit() {
        // load the initial element list
        this.filteredData.next(this.selectConfig.data.slice());

        if (this.selectConfig.dynamic) {
            this.fetchDataFromBeIfNoData();
        }

        // listen for search field value changes
        this.selFilterCtrl.valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                if (this.selectConfig.dynamic) {
                    this.fetchDataFromBeIfNoData();
                } else {
                    this.filterData();
                }
            });
    }

    protected fetchDataFromBeIfNoData() {
        // get the search keyword
        let search = this.selFilterCtrl.value;
        if (this.selectConfig.dynamic.initMotif) {
            search = this.selectConfig.dynamic.initMotif;
        }

        let newParams = {motif: search, ...this.selectConfig.dynamic.params};
        this.http
            .get(this.selectConfig.dynamic.url, newParams)
            .subscribe((data) => {
                if (this.selectConfig.dynamic?.initMotif) {
                    this.selectConfig.dynamic.initMotif = '';
                }

                this.curData = data;
                this.filteredData.next(data);
            });
    }

    ngAfterViewInit() {
        // this.setInitialValue();
        this.onChange(this.value);
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    /**
     * Sets the initial value after the filterData are loaded initially
     */
    protected setInitialValue() {
        this.filteredData
            .pipe(take(1), takeUntil(this._onDestroy))
            .subscribe(() => {
                // setting the compareWith property to a comparison function
                // triggers initializing the selection according to the initial value of
                // the form control (i.e. _initializeSelection())
                // this needs to be done after the filterData are loaded initially
                // and after the mat-option elements are available
                this.singleSelect.compareWith = (a, b) => a && b && a.id === b.id;
            });
    }

    protected filterData() {
        if (!this.selectConfig.data) {
            return;
        }
        // get the search keyword
        let search = this.misc.removeAccents(this.selFilterCtrl.value);

        if (!search) {
            this.filteredData.next(this.selectConfig.data.slice());
            return;
        } else {
            search = search.toString().toLowerCase();
        }
        // filter the elements
        this.filteredData.next(
            this.selectConfig.data.filter(
                (el) => {
                    return this.misc.removeAccents(el[this.selectConfig.displKey].toString()).toLowerCase().indexOf(search) > -1;
                }
            )
        );
    }

    emitSelVal() {
        if (this.selectConfig.dynamic) {
            let selObj = this.curData.filter((d) => d.id == this.value);

            if (selObj.length) {
                this.selectedVal.emit(selObj[0]);
            }
        } else {
            this.selectedVal.emit(this.value);
        }
    }

    // ==================================================================================================================
    /**
     * Holds the current value initial val
     */
    value = 0;

    /**
     * Invoked when the model has been changed
     */
    onChange: (_: any) => void = (_: any) => {
        if (this.value && this.selectConfig.data.length) {
            let selElem = this.selectConfig.data.filter((e) => e.id == this.value);
            this.value = selElem && selElem.length ? selElem[0].id : 0;
            // this.isloaded = false;
            // timer(50).subscribe(() => (this.isloaded = true));
        }
    };

    /**
     * Invoked when the model has been touched
     */
    onTouched: () => void = () => {
    };

    /**
     * Method that is invoked on an update of a model.
     */
    updateChanges() {
        this.onChange(this.value);
    }

    ///////////////
    // OVERRIDES //
    ///////////////

    /**
     * Writes a new item to the element.
     * @param value the value
     */
    writeValue(value): void {
        if (
            !value &&
            this.selectConfig.selFirstIfOneElem &&
            this.selectConfig.data &&
            this.selectConfig.data.length === 1
        ) {
            this.value = this.selectConfig.data[0].id;
            this.emitSelVal();
        } else if (
            this.selectConfig.selFirst &&
            this.selectConfig.data &&
            this.selectConfig.data.length
        ) {
            this.value = this.selectConfig.data[0].id;
            this.emitSelVal();
        } else {
            this.value = value;
        }
        this.updateChanges();
    }

    /**
     * Registers a callback function that should be called when the control's value changes in the UI.
     * @param fn
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Registers a callback function that should be called when the control receives a blur event.
     * @param fn
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
}

export interface ISelectNgxMat {
    data;
    placeHolder;
    displKey: 'itemName' | 'lib' | 'libWithCode' | 'code';
    required?;
    classe?;
    disabled?;
    widthPx?;
    selFirstIfOneElem?;
    selFirst?;
    noEmptyVal?: boolean;
    dynamic?: {
        initMotif?: any;
        url;
        params?;
    };
    isAppearanceFill?;
}
